import React, { useCallback, useEffect, useState } from 'react';
import LoaderOverlay from './LoaderOverlay';
import { showAppAlert } from '../utils/alertService';
import '../styles/formStyles.css';

const API_URL = process.env.REACT_APP_API_BASE_URL;

function maskPhone(value) {
  if (!value) return '';

  const digits = String(value).replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  }
  if (digits.length === 10) {
    return digits.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  }

  return value;
}

function InactiveClientsList() {
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [clients, setClients] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const token = localStorage.getItem('token');

  const fetchCompanies = useCallback(async () => {
    setLoadingCompanies(true);

    try {
      const response = await fetch(`${API_URL}/admin/lojistas/ativos`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao carregar os lojistas ativos.');
      }

      setCompanies(Array.isArray(data) ? data : []);
    } catch (error) {
      setCompanies([]);
      showAppAlert('error', error.message || 'Erro ao carregar os lojistas ativos.');
    } finally {
      setLoadingCompanies(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const fetchInactiveClients = useCallback(async (companyId) => {
    setLoadingClients(true);
    setHasSearched(false);

    try {
      const response = await fetch(
        `${API_URL}/admin/clients/inactive?company_id=${encodeURIComponent(companyId)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        }
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao carregar os clientes inativos.');
      }

      setClients(Array.isArray(data) ? data : []);
    } catch (error) {
      setClients([]);
      showAppAlert('error', error.message || 'Erro ao carregar os clientes inativos.');
    } finally {
      setLoadingClients(false);
      setHasSearched(true);
    }
  }, [token]);

  const handleCompanyChange = (event) => {
    const companyId = event.target.value;
    setSelectedCompanyId(companyId);
    setClients([]);
    setHasSearched(false);

    if (companyId) {
      fetchInactiveClients(companyId);
    }
  };

  return (
    <section className="dashboard-section">
      <LoaderOverlay
        isLoading={loadingCompanies || loadingClients}
        message={loadingCompanies ? 'Carregando lojistas...' : 'Carregando clientes inativos...'}
      />

      <h3>Clientes inativos</h3>

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          gap: '16px',
          marginBottom: '20px',
          padding: '15px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
        }}
      >
        <div className="filter-group" style={{ minWidth: '280px' }}>
          <label htmlFor="inactiveClientsCompany">Lojista:</label>
          <select
            id="inactiveClientsCompany"
            className="input-style"
            value={selectedCompanyId}
            onChange={handleCompanyChange}
            disabled={loadingCompanies || loadingClients}
          >
            <option value="">
              {loadingCompanies ? 'Carregando lojistas...' : 'Selecione um lojista'}
            </option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.nome}
              </option>
            ))}
          </select>
        </div>
      </div>

      {companies.length === 0 && !loadingCompanies && (
        <p>Nenhum lojista ativo encontrado.</p>
      )}

      {clients.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>E-mail</th>
                <th>Telefone</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((client, index) => (
                <tr key={`${client.email || 'cliente'}-${client.telefone || index}-${index}`}>
                  <td>{client.nome || ''}</td>
                  <td>{client.email || ''}</td>
                  <td>{maskPhone(client.telefone)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedCompanyId && hasSearched && clients.length === 0 && !loadingClients && (
        <p>Nenhum cliente inativo encontrado.</p>
      )}
    </section>
  );
}

export default InactiveClientsList;
